import type { ProgramTemplate } from "./templateSchema.js";
import { repeatWeek, slot } from "./templateAuthoring.js";

// One hand-authored program template. Split out of templateData.ts
// (SP-TEMPLATES, 2026-07-27) so every file in this cluster clears the
// 500-line cap on its own, without invoking the data-file exemption.
// At ~355 lines this is over the 350 soft-warning: it is a single
// irreducible hand-written template, so there is no seam inside it.

// Push / Pull / Legs at 6 sessions / week × 4 weeks. Each split is
// a top-set + back-off + isolation block. Lower volume on the second
// rotation (week 2 + 4) to bias recovery; the percentages drop a tick.
export const pushPullLegs: ProgramTemplate = {
  id: "tpl-prog-push-pull-legs",
  name: {
    en: "Push / Pull / Legs",
    it: "Push / Pull / Legs",
    fr: "Push / Pull / Legs",
    de: "Push / Pull / Legs",
    ru: "Push / Pull / Legs",
  },
  description: {
    en: "Six sessions per week split into two rotations of Push / Pull / Legs. Compound first, back-off second, isolation last. Four-week cycle with a small de-load on weeks 2 and 4.",
    it: "Sei sessioni a settimana divise in due rotazioni di Push / Pull / Legs. Fondamentale prima, back-off poi, isolamento alla fine. Ciclo di quattro settimane con un piccolo deload alle settimane 2 e 4.",
    fr: "Six séances par semaine réparties en deux rotations de Push / Pull / Legs. Polyarticulaire d'abord, back-off ensuite, isolation à la fin. Cycle de quatre semaines avec un léger deload aux semaines 2 et 4.",
    de: "Sechs Einheiten pro Woche, aufgeteilt in zwei Rotationen Push / Pull / Legs. Erst Grundübung, dann Back-off, zuletzt Isolation. Vier-Wochen-Zyklus mit kleinem Deload in Woche 2 und 4.",
    ru: "Шесть тренировок в неделю, разбитые на две ротации Push / Pull / Legs. Сначала базовое, потом бэк-офф, в конце изоляция. Четырёхнедельный цикл с лёгкой разгрузкой на неделях 2 и 4.",
  },
  difficulty: "intermediate",
  tags: ["hypertrophy", "ppl", "split", "6-day"],
  cycleWeeks: 4,
  sessionsPerWeek: 6,
  sessions: [
    {
      id: "tpl-prog-ppl-push-1",
      name: {
        en: "Push #1 — Bench focus",
        it: "Push #1 — Focus Bench",
        fr: "Push #1 — Focus Bench",
        de: "Push #1 — Bench-Fokus",
        ru: "Push #1 — Фокус Bench",
      },
      slots: [
        slot(
          "tpl-prog-ppl-p1-bench",
          0,
          "bench-press",
          "Bench Press",
          [
            {
              sets: [
                { reps: 5, intensity: { kind: "percent-of-max", pct: 82 } },
                { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
              ],
            },
            {
              sets: [
                { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
                { reps: 5, intensity: { kind: "percent-of-max", pct: 72 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 65 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 65 } },
              ],
            },
            {
              sets: [
                { reps: 5, intensity: { kind: "percent-of-max", pct: 84 } },
                { reps: 5, intensity: { kind: "percent-of-max", pct: 82 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 72 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 72 } },
              ],
            },
            {
              sets: [
                { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
                { reps: 5, intensity: { kind: "percent-of-max", pct: 72 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 65 } },
                { reps: 8, intensity: { kind: "percent-of-max", pct: 65 } },
              ],
            },
          ],
          {
            instructions: {
              en: "Weeks 2 and 4 are light — same volume, drop ~7 % off the percentages.",
              it: "Le settimane 2 e 4 sono leggere — stesso volume, togli ~7 % dalle percentuali.",
              fr: "Les semaines 2 et 4 sont légères — même volume, retire ~7 % des pourcentages.",
              de: "Woche 2 und 4 sind leicht — gleiches Volumen, ~7 % von den Prozentsätzen abziehen.",
              ru: "Недели 2 и 4 — лёгкие: тот же объём, проценты убавь примерно на 7 %.",
            },
          },
        ),
        slot(
          "tpl-prog-ppl-p1-incline",
          1,
          "incline-bench-press",
          "Incline Bench Press",
          repeatWeek(4, [
            { reps: 10, intensity: { kind: "percent-of-max", pct: 60 } },
            { reps: 10, intensity: { kind: "percent-of-max", pct: 60 } },
            { reps: 10, intensity: { kind: "percent-of-max", pct: 60 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-p1-lateral",
          2,
          "lateral-raise",
          "Lateral Raise",
          repeatWeek(4, [
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
          ]),
        ),
      ],
    },
    {
      id: "tpl-prog-ppl-pull-1",
      name: {
        en: "Pull #1 — Deadlift focus",
        it: "Pull #1 — Focus Deadlift",
        fr: "Pull #1 — Focus Deadlift",
        de: "Pull #1 — Deadlift-Fokus",
        ru: "Pull #1 — Фокус Deadlift",
      },
      slots: [
        slot(
          "tpl-prog-ppl-pl1-dead",
          0,
          "deadlift",
          "Deadlift",
          repeatWeek(4, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 70 } },
          ]),
          {
            instructions: {
              en: "Reset every rep. No touch-and-go.",
              it: "Resetta ogni ripetizione. Niente touch-and-go.",
              fr: "Reset à chaque rep. Pas de touch-and-go.",
              de: "Jede Wiederholung absetzen. Kein Touch-and-go.",
              ru: "Сбрасывай каждый повтор. Никаких touch-and-go.",
            },
          },
        ),
        slot(
          "tpl-prog-ppl-pl1-row",
          1,
          "barbell-row",
          "Barbell Row",
          repeatWeek(4, [
            { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-pl1-pull",
          2,
          "pull-up",
          "Pull-up",
          repeatWeek(4, [
            { reps: 8, intensity: { kind: "rpe", target: 8 } },
            { reps: 8, intensity: { kind: "rpe", target: 8 } },
            { reps: 8, intensity: { kind: "rpe", target: 8 } },
          ]),
          { amrapLastSet: true },
        ),
      ],
    },
    {
      id: "tpl-prog-ppl-legs-1",
      name: {
        en: "Legs #1 — Squat focus",
        it: "Legs #1 — Focus Squat",
        fr: "Legs #1 — Focus Squat",
        de: "Legs #1 — Squat-Fokus",
        ru: "Legs #1 — Фокус Squat",
      },
      slots: [
        slot(
          "tpl-prog-ppl-l1-squat",
          0,
          "back-squat",
          "Back Squat",
          repeatWeek(4, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 78 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-l1-leg-press",
          1,
          "leg-press",
          "Leg Press",
          repeatWeek(4, [
            { reps: 12, intensity: { kind: "percent-of-max", pct: 60 } },
            { reps: 12, intensity: { kind: "percent-of-max", pct: 60 } },
            { reps: 12, intensity: { kind: "percent-of-max", pct: 60 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-l1-rdl",
          2,
          "romanian-deadlift",
          "Romanian Deadlift",
          repeatWeek(4, [
            { reps: 8, intensity: { kind: "rpe", target: 8 } },
            { reps: 8, intensity: { kind: "rpe", target: 8 } },
            { reps: 8, intensity: { kind: "rpe", target: 8 } },
          ]),
        ),
      ],
    },
    {
      id: "tpl-prog-ppl-push-2",
      name: {
        en: "Push #2 — OHP focus",
        it: "Push #2 — Focus OHP",
        fr: "Push #2 — Focus OHP",
        de: "Push #2 — OHP-Fokus",
        ru: "Push #2 — Фокус OHP",
      },
      slots: [
        slot(
          "tpl-prog-ppl-p2-ohp",
          0,
          "overhead-press",
          "Overhead Press",
          repeatWeek(4, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 78 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 68 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 68 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-p2-incline",
          1,
          "incline-bench-press",
          "Incline Bench Press",
          repeatWeek(4, [
            { reps: 8, intensity: { kind: "percent-of-max", pct: 65 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 65 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 65 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-p2-dip",
          2,
          "dip",
          "Dip",
          repeatWeek(4, [
            { reps: 10, intensity: { kind: "rpe", target: 8 } },
            { reps: 10, intensity: { kind: "rpe", target: 8 } },
            { reps: 10, intensity: { kind: "rpe", target: 8 } },
          ]),
          { amrapLastSet: true },
        ),
      ],
    },
    {
      id: "tpl-prog-ppl-pull-2",
      name: {
        en: "Pull #2 — Row focus",
        it: "Pull #2 — Focus Row",
        fr: "Pull #2 — Focus Row",
        de: "Pull #2 — Row-Fokus",
        ru: "Pull #2 — Фокус Row",
      },
      slots: [
        slot(
          "tpl-prog-ppl-pl2-row",
          0,
          "cable-row",
          "Cable Row",
          repeatWeek(4, [
            { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 70 } },
            { reps: 10, intensity: { kind: "percent-of-max", pct: 65 } },
            { reps: 10, intensity: { kind: "percent-of-max", pct: 65 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-pl2-pulldown",
          1,
          "lat-pulldown",
          "Lat Pulldown",
          repeatWeek(4, [
            { reps: 10, intensity: { kind: "percent-of-max", pct: 65 } },
            { reps: 10, intensity: { kind: "percent-of-max", pct: 65 } },
            { reps: 10, intensity: { kind: "percent-of-max", pct: 65 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-pl2-curl",
          2,
          "biceps-curl",
          "Biceps Curl",
          repeatWeek(4, [
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
          ]),
        ),
      ],
    },
    {
      id: "tpl-prog-ppl-legs-2",
      name: {
        en: "Legs #2 — Posterior chain",
        it: "Legs #2 — Catena posteriore",
        fr: "Legs #2 — Chaîne postérieure",
        de: "Legs #2 — Hintere Kette",
        ru: "Legs #2 — Задняя цепь",
      },
      slots: [
        slot(
          "tpl-prog-ppl-l2-rdl",
          0,
          "romanian-deadlift",
          "Romanian Deadlift",
          repeatWeek(4, [
            { reps: 6, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 6, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 8, intensity: { kind: "percent-of-max", pct: 68 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-l2-front",
          1,
          "front-squat",
          "Front Squat",
          repeatWeek(4, [
            { reps: 6, intensity: { kind: "percent-of-max", pct: 70 } },
            { reps: 6, intensity: { kind: "percent-of-max", pct: 70 } },
            { reps: 6, intensity: { kind: "percent-of-max", pct: 70 } },
          ]),
        ),
        slot(
          "tpl-prog-ppl-l2-curl",
          2,
          "leg-curl",
          "Leg Curl",
          repeatWeek(4, [
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
            { reps: 12, intensity: { kind: "rpe", target: 8 } },
          ]),
        ),
      ],
    },
  ],
};
